import { api } from '@/lib/api/client';
import type {
  Listing,
  ListingDetail,
  Destination,
  AvailabilityCalendarDay,
  Booking,
  BookingPayload,
  WishlistItem,
  WishlistCollection,
  Review,
  ReviewPayload,
  TouristProfile,
  GearItem,
  GearRentalPayload,
  PriceAlertPayload,
  SearchParams,
  Addon,
  TouristNotification,
  GamificationTier,
  CatalogReward,
  EarnRule,
  Club,
  ClubSession,
  ClubMember,
  ClubLeaderboardEntry,
  CreateClubPayload,
  FeedPost,
  FeedComment,
  Story,
  CompanionProfile,
  CompanionDecidePayload,
  Expedition,
  Conversation,
  UOChatMessage,
  TrailConnectPulse,
  GamificationLeaderboardEntry,
  PointTransaction,
  WeatherCondition,
  MarketplaceStats,
  LocalEvent,
} from '../types';

// ── Listings ───────────────────────────────────────────────────────────────

async function getFeatured(): Promise<Listing[]> {
  const { data } = await api.get<Listing[]>('/marketplace/featured');
  return data;
}

async function getDestinations(): Promise<Destination[]> {
  const { data } = await api.get<Destination[]>('/marketplace/destinations');
  return data;
}

async function searchListings(params: SearchParams): Promise<Listing[]> {
  const { data } = await api.get<Listing[]>('/marketplace/search', { params });
  return data;
}

async function getListing(slug: string): Promise<ListingDetail> {
  const { data } = await api.get<ListingDetail>(`/marketplace/listings/${slug}`);
  return data;
}

async function checkAvailability(
  slug: string,
  checkIn: string,
  checkOut: string,
  guests?: number,
): Promise<{ available: boolean; nights: number; total_cents: number }> {
  const { data } = await api.get(`/marketplace/listings/${slug}/availability`, {
    params: { check_in: checkIn, check_out: checkOut, guests },
  });
  return data;
}

async function getAvailabilityCalendar(
  slug: string,
  year: number,
  month: number,
): Promise<AvailabilityCalendarDay[]> {
  const { data } = await api.get<AvailabilityCalendarDay[]>(
    `/marketplace/listings/${slug}/availability/calendar`,
    { params: { year, month } },
  );
  return data;
}

async function getListingPricing(
  slug: string,
  params: { check_in?: string; check_out?: string; guests?: number },
): Promise<{ total_cents: number; breakdown: Record<string, number> }> {
  const { data } = await api.get(`/marketplace/listings/${slug}/pricing`, { params });
  return data;
}

async function getListingAddons(slug: string): Promise<Addon[]> {
  const { data } = await api.get<Addon[]>(`/marketplace/listings/${slug}/addons`);
  return data;
}

async function getListingReviews(
  slug: string,
  params?: { sort?: string; page?: number; per_page?: number },
): Promise<{ reviews: Review[]; total: number }> {
  const { data } = await api.get(`/marketplace/listings/${slug}/reviews`, { params });
  return data;
}

async function getHouseboatMeta(id: string): Promise<Record<string, unknown>> {
  const { data } = await api.get(`/marketplace/listings/${id}/houseboat-meta`);
  return data;
}

async function getNearby(lat: number, lng: number): Promise<Listing[]> {
  const { data } = await api.get<Listing[]>('/marketplace/nearby', {
    params: { lat, lng },
  });
  return data;
}

// ── Auth ───────────────────────────────────────────────────────────────────

async function loginTourist(
  email: string,
  password: string,
): Promise<{ access_token: string; refresh_token: string; user: TouristProfile }> {
  const { data } = await api.post('/marketplace/auth/login', { email, password });
  return data;
}

async function registerTourist(payload: {
  display_name: string;
  email: string;
  password: string;
  phone?: string;
}): Promise<{ access_token: string; refresh_token: string; user: TouristProfile }> {
  const { data } = await api.post('/marketplace/auth/register', payload);
  return data;
}

// ── Profile ────────────────────────────────────────────────────────────────

async function getProfile(): Promise<TouristProfile> {
  const { data } = await api.get<TouristProfile>('/marketplace/profile');
  return data;
}

async function updateProfile(
  payload: Partial<Omit<TouristProfile, 'id' | 'email' | 'points' | 'tier'>>,
): Promise<TouristProfile> {
  const { data } = await api.put<TouristProfile>('/marketplace/profile', payload);
  return data;
}

async function uploadAvatar(file: File): Promise<{ avatar_url: string }> {
  const form = new FormData();
  form.append('avatar', file);
  const { data } = await api.post('/marketplace/profile/avatar', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

async function getMyReviews(): Promise<Review[]> {
  const { data } = await api.get<Review[]>('/marketplace/profile/reviews');
  return data;
}

// ── Bookings ───────────────────────────────────────────────────────────────

async function createBooking(payload: BookingPayload): Promise<Booking> {
  const { data } = await api.post<Booking>('/marketplace/bookings', payload);
  return data;
}

async function listBookings(): Promise<Booking[]> {
  const { data } = await api.get<Booking[]>('/marketplace/bookings');
  return data;
}

async function getBooking(id: string): Promise<Booking> {
  const { data } = await api.get<Booking>(`/marketplace/bookings/${id}`);
  return data;
}

async function cancelBooking(id: string): Promise<Booking> {
  const { data } = await api.put<Booking>(`/marketplace/bookings/${id}/cancel`);
  return data;
}

async function lookupBooking(reference: string, email: string): Promise<Booking> {
  const { data } = await api.get<Booking>('/marketplace/bookings/lookup', {
    params: { reference, email },
  });
  return data;
}

async function initiateCheckout(
  bookingId: string,
): Promise<{ payment_url: string; order_id: string }> {
  const { data } = await api.post(`/marketplace/checkout/${bookingId}`);
  return data;
}

// ── Wishlists ──────────────────────────────────────────────────────────────

async function listWishlists(): Promise<WishlistItem[]> {
  const { data } = await api.get<WishlistItem[]>('/marketplace/wishlists');
  return data;
}

async function addWishlist(listingId: string): Promise<void> {
  await api.post('/marketplace/wishlists', { listing_id: listingId });
}

async function removeWishlist(listingId: string): Promise<void> {
  await api.delete(`/marketplace/wishlists/${listingId}`);
}

async function listWishlistCollections(): Promise<WishlistCollection[]> {
  const { data } = await api.get<WishlistCollection[]>('/marketplace/wishlists/collections');
  return data;
}

async function createWishlistCollection(name: string): Promise<WishlistCollection> {
  const { data } = await api.post<WishlistCollection>('/marketplace/wishlists/collections', { name });
  return data;
}

// ── Reviews ────────────────────────────────────────────────────────────────

async function createReview(payload: ReviewPayload): Promise<Review> {
  const { data } = await api.post<Review>('/marketplace/reviews', payload);
  return data;
}

async function updateReview(
  id: string,
  payload: Partial<Pick<ReviewPayload, 'rating' | 'comment'>>,
): Promise<Review> {
  const { data } = await api.put<Review>(`/marketplace/reviews/${id}`, payload);
  return data;
}

async function markHelpful(id: string): Promise<void> {
  await api.post(`/marketplace/reviews/${id}/helpful`);
}

// ── Gear ───────────────────────────────────────────────────────────────────

async function listGear(params?: { category?: string; q?: string; page?: number }): Promise<GearItem[]> {
  const { data } = await api.get<{ items: GearItem[] }>('/marketplace/gear', { params });
  return data.items ?? [];
}

async function getGear(slug: string): Promise<GearItem> {
  const { data } = await api.get<GearItem>(`/marketplace/gear/${slug}`);
  return data;
}

async function rentGear(id: string, payload: GearRentalPayload): Promise<{ rental_id: string }> {
  const { data } = await api.post(`/marketplace/gear/${id}/rent`, payload);
  return data;
}

async function getGearAvailability(
  id: string,
  params: { start_date: string; end_date: string },
): Promise<{ available: boolean; quantity_left: number }> {
  const { data } = await api.get(`/marketplace/gear/${id}/availability`, { params });
  return data;
}

async function myGearRentals(): Promise<unknown[]> {
  const { data } = await api.get('/marketplace/gear/my-rentals');
  return data;
}

// ── Price Alerts ───────────────────────────────────────────────────────────

async function createPriceAlert(payload: PriceAlertPayload): Promise<{ id: string }> {
  const { data } = await api.post('/marketplace/price-alerts', payload);
  return data;
}

async function deletePriceAlert(id: string): Promise<void> {
  await api.delete(`/marketplace/price-alerts/${id}`);
}

async function listPriceAlerts(): Promise<PriceAlertPayload[]> {
  const { data } = await api.get('/marketplace/price-alerts');
  return data;
}

// ── Notifications ──────────────────────────────────────────────────────────

async function listNotifications(params?: {
  unread_only?: boolean;
  page?: number;
  per_page?: number;
}): Promise<TouristNotification[]> {
  const { data } = await api.get<TouristNotification[]>('/marketplace/notifications', { params });
  return data;
}

async function markAllNotificationsRead(): Promise<void> {
  await api.put('/marketplace/notifications/read-all');
}

async function markNotificationRead(id: string): Promise<void> {
  await api.put(`/marketplace/notifications/${id}/read`);
}

// ── Gamification ───────────────────────────────────────────────────────────

async function listTiers(): Promise<GamificationTier[]> {
  const { data } = await api.get<GamificationTier[]>('/marketplace/gamification/tiers');
  return data;
}

async function listRewardsCatalog(): Promise<CatalogReward[]> {
  const { data } = await api.get<CatalogReward[]>('/marketplace/gamification/catalog');
  return data;
}

async function listEarnRules(): Promise<EarnRule[]> {
  const { data } = await api.get<EarnRule[]>('/marketplace/gamification/earn-rules');
  return data;
}

// ── Clubs ─────────────────────────────────────────────────────────────────

async function listClubs(params?: {
  activity?: string;
  city?: string;
  level?: string;
  open_to_visitors?: boolean;
  q?: string;
  page?: number;
  per_page?: number;
}): Promise<Club[]> {
  const { data } = await api.get('/marketplace/clubs', { params });
  return data.data ?? [];
}

async function getClub(slug: string): Promise<Club> {
  const { data } = await api.get(`/marketplace/clubs/${slug}`);
  return data.data;
}

async function getClubSessions(slug: string): Promise<ClubSession[]> {
  const { data } = await api.get(`/marketplace/clubs/${slug}/sessions`);
  return data.data ?? [];
}

async function rsvpSession(clubSlug: string, sessionId: string): Promise<void> {
  await api.post(`/marketplace/clubs/${clubSlug}/sessions/${sessionId}/rsvp`);
}

async function cancelRsvp(clubSlug: string, sessionId: string): Promise<void> {
  await api.delete(`/marketplace/clubs/${clubSlug}/sessions/${sessionId}/rsvp`);
}

async function getClubMembers(slug: string): Promise<ClubMember[]> {
  const { data } = await api.get(`/marketplace/clubs/${slug}/members`);
  return data.data ?? [];
}

async function getClubsLeaderboard(params?: {
  city?: string;
  activity?: string;
  period?: 'week' | 'month' | 'all';
}): Promise<ClubLeaderboardEntry[]> {
  const { data } = await api.get('/marketplace/clubs/leaderboard', { params });
  return data.data ?? [];
}

async function createClub(payload: CreateClubPayload): Promise<Club> {
  const { data } = await api.post('/marketplace/clubs', payload);
  return data.data;
}

// ── Community Feed ─────────────────────────────────────────────────────────

async function getFeed(params?: { page?: number; per_page?: number }): Promise<FeedPost[]> {
  const { data } = await api.get('/marketplace/community/feed', { params });
  return data.data ?? [];
}

async function getFeedPost(id: string): Promise<FeedPost> {
  const { data } = await api.get(`/marketplace/community/feed/${id}`);
  return data.data;
}

async function likeFeedPost(id: string): Promise<{ liked: boolean; likes_count: number }> {
  const { data } = await api.post(`/marketplace/community/feed/${id}/like`);
  return data.data;
}

async function getFeedComments(
  id: string,
  params?: { page?: number; per_page?: number },
): Promise<FeedComment[]> {
  const { data } = await api.get(`/marketplace/community/feed/${id}/comments`, { params });
  return data.data ?? [];
}

async function addFeedComment(id: string, text: string): Promise<FeedComment> {
  const { data } = await api.post(`/marketplace/community/feed/${id}/comments`, { text });
  return data.data;
}

// ── Stories ────────────────────────────────────────────────────────────────

async function getStories(params?: { page?: number; per_page?: number }): Promise<Story[]> {
  const { data } = await api.get('/marketplace/community/stories', { params });
  return data.data ?? [];
}

async function getStory(slug: string): Promise<Story> {
  const { data } = await api.get(`/marketplace/community/stories/${slug}`);
  return data.data;
}

async function reactToStory(slug: string, emoji: string): Promise<void> {
  await api.post(`/marketplace/community/stories/${slug}/reactions`, { emoji });
}

// ── Companions ─────────────────────────────────────────────────────────────

async function getCompanionDeck(): Promise<CompanionProfile[]> {
  const { data } = await api.get('/marketplace/community/companions');
  return data.data ?? [];
}

async function getMyMatches(): Promise<CompanionProfile[]> {
  const { data } = await api.get('/marketplace/community/companions/matches');
  return data.data ?? [];
}

async function decideCompanion(payload: CompanionDecidePayload): Promise<{ matched: boolean }> {
  const { data } = await api.post('/marketplace/community/companions/decide', payload);
  return data.data;
}

// ── Expeditions ────────────────────────────────────────────────────────────

async function getExpeditions(): Promise<Expedition[]> {
  const { data } = await api.get('/marketplace/expeditions');
  return data.data ?? [];
}

async function getExpedition(slug: string): Promise<Expedition> {
  const { data } = await api.get(`/marketplace/expeditions/${slug}`);
  return data.data;
}

async function joinExpeditionVirtually(slug: string): Promise<void> {
  await api.post(`/marketplace/expeditions/${slug}/join-virtually`);
}

async function cheerExpedition(slug: string): Promise<void> {
  await api.post(`/marketplace/expeditions/${slug}/cheers`);
}

// ── Conversations / Chat ───────────────────────────────────────────────────

async function listConversations(): Promise<Conversation[]> {
  const { data } = await api.get('/marketplace/conversations');
  return data.data ?? [];
}

async function startConversation(targetTouristId: string): Promise<Conversation> {
  const { data } = await api.post('/marketplace/conversations', {
    target_tourist_id: targetTouristId,
  });
  return data.data;
}

async function getConversationMessages(
  slug: string,
  params?: { page?: number; per_page?: number },
): Promise<UOChatMessage[]> {
  const { data } = await api.get(`/marketplace/conversations/${slug}/messages`, { params });
  return data.data ?? [];
}

async function sendMessage(slug: string, text: string): Promise<UOChatMessage> {
  const { data } = await api.post(`/marketplace/conversations/${slug}/messages`, { text });
  return data.data;
}

// ── TrailConnect Pulse ─────────────────────────────────────────────────────

async function getTrailConnectPulse(): Promise<TrailConnectPulse> {
  const { data } = await api.get('/trailconnect/pulse');
  return data;
}

// ── Gamification leaderboard + history ────────────────────────────────────

async function getGamificationLeaderboard(params?: {
  period?: 'week' | 'month' | 'all';
  page?: number;
  per_page?: number;
}): Promise<GamificationLeaderboardEntry[]> {
  return api.get('/marketplace/gamification/leaderboard', { params }).then((r) => r.data.data ?? []);
}

async function getPointHistory(params?: {
  page?: number;
  per_page?: number;
}): Promise<PointTransaction[]> {
  return api.get('/marketplace/gamification/history', { params }).then((r) => r.data.data ?? []);
}

// ── Weather ────────────────────────────────────────────────────────────────

async function getWeather(cities: string[]): Promise<WeatherCondition[]> {
  const { data } = await api.get<WeatherCondition[]>('/weather', {
    params: { cities: cities.join(',') },
  });
  return data;
}

// ── Marketplace Stats + Local Events ──────────────────────────────────────

async function getStats(): Promise<MarketplaceStats> {
  const { data } = await api.get<MarketplaceStats>('/marketplace/stats');
  return data;
}

async function getLocalEvents(): Promise<LocalEvent[]> {
  const { data } = await api.get<LocalEvent[]>('/marketplace/events');
  return data;
}

// ── Export ─────────────────────────────────────────────────────────────────

export const marketplaceService = {
  // Listings
  getFeatured,
  getDestinations,
  searchListings,
  getListing,
  checkAvailability,
  getAvailabilityCalendar,
  getListingPricing,
  getListingAddons,
  getListingReviews,
  getHouseboatMeta,
  getNearby,
  // Auth
  loginTourist,
  registerTourist,
  // Profile
  getProfile,
  updateProfile,
  uploadAvatar,
  getMyReviews,
  // Bookings
  createBooking,
  listBookings,
  getBooking,
  cancelBooking,
  lookupBooking,
  initiateCheckout,
  // Wishlists
  listWishlists,
  addWishlist,
  removeWishlist,
  listWishlistCollections,
  createWishlistCollection,
  // Reviews
  createReview,
  updateReview,
  markHelpful,
  // Gear
  listGear,
  getGear,
  rentGear,
  getGearAvailability,
  myGearRentals,
  // Price alerts
  createPriceAlert,
  deletePriceAlert,
  listPriceAlerts,
  // Notifications
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  // Gamification
  listTiers,
  listRewardsCatalog,
  listEarnRules,
  // Clubs
  listClubs,
  getClub,
  getClubSessions,
  rsvpSession,
  cancelRsvp,
  getClubMembers,
  getClubsLeaderboard,
  createClub,
  // Community Feed
  getFeed,
  getFeedPost,
  likeFeedPost,
  getFeedComments,
  addFeedComment,
  // Stories
  getStories,
  getStory,
  reactToStory,
  // Companions
  getCompanionDeck,
  getMyMatches,
  decideCompanion,
  // Expeditions
  getExpeditions,
  getExpedition,
  joinExpeditionVirtually,
  cheerExpedition,
  // Conversations / Chat
  listConversations,
  startConversation,
  getConversationMessages,
  sendMessage,
  // TrailConnect Pulse
  getTrailConnectPulse,
  // Gamification leaderboard + history
  getGamificationLeaderboard,
  getPointHistory,
  // Weather
  getWeather,
  // Marketplace Stats + Local Events
  getStats,
  getLocalEvents,
};
